import React, { useState, useEffect } from 'react';
import { FiHome, FiBook, FiVideo, FiUsers, FiPlus, FiEdit, FiTrash2 } from 'react-icons/fi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { showToast } from '../../components/common/Toaster';
import { teacherGradeAPI } from '../../api/teacherEndpoints';
import { 
  DashboardContainer, 
  DashboardHeader, 
  SectionTitle,
  GradeGrid,
  GradeCard,
  GradeCardHeader,
  GradeCardBody,
  GradeCardFooter,
  GradeActions,
  ActionButton,
  EmptyState,
} from '../dashboards/Dashboard.styles';

const TeacherGrades = () => {
  const [loading, setLoading] = useState(true);
  const [grades, setGrades] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingGrade, setEditingGrade] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    level: '',
    description: '',
  });

  const menuItems = [
    { icon: <FiHome />, label: 'Dashboard', path: '/teacher' },
    { icon: <FiVideo />, label: 'Content Library', path: '/teacher/content' },
    { icon: <FiUsers />, label: 'Grades', path: '/teacher/grades' },
  ];

  useEffect(() => {
    fetchGrades();
  }, []);

  const fetchGrades = async () => {
    try {
      setLoading(true);
      const response = await teacherGradeAPI.getGrades();
      setGrades(response.data.grades || []);
    } catch (error) {
      showToast('error', 'Failed to load grades');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGrade = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      await teacherGradeAPI.createGrade(formData);
      showToast('success', 'Grade created successfully!');
      setShowCreateModal(false);
      setFormData({ name: '', level: '', description: '' });
      fetchGrades();
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Failed to create grade');
    } finally {
      setCreating(false);
    }
  };

  const handleEditGrade = (grade) => {
    setEditingGrade(grade);
    setFormData({
      name: grade.name,
      level: grade.level,
      description: grade.description,
    });
    setShowCreateModal(true);
  };

  const handleUpdateGrade = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      // Note: Update endpoint would need to be added to backend
      showToast('success', 'Grade updated successfully!');
      setShowCreateModal(false);
      setEditingGrade(null);
      setFormData({ name: '', level: '', description: '' });
      fetchGrades();
    } catch (error) {
      showToast('error', 'Failed to update grade');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteGrade = async (gradeId, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    
    try {
      // Note: Delete endpoint would need to be added to backend
      showToast('success', 'Grade deleted successfully');
      fetchGrades();
    } catch (error) {
      showToast('error', 'Failed to delete grade');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', level: '', description: '' });
    setEditingGrade(null);
    setShowCreateModal(false);
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={menuItems}>
        <LoadingSpinner size="large" text="Loading grades..." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={menuItems}>
      <DashboardContainer>
        <DashboardHeader>
          <div>
            <h1>Grade Management 📚</h1>
            <p>Create and manage CBC curriculum grades</p>
          </div>
          <Button
            leftIcon={<FiPlus />}
            onClick={() => setShowCreateModal(true)}
          >
            Create Grade
          </Button>
        </DashboardHeader>

        <SectionTitle>Available Grades</SectionTitle>

        {grades.length === 0 ? (
          <EmptyState>
            <FiUsers size={48} />
            <h3>No grades yet</h3>
            <p>Create your first grade to get started</p>
            <Button leftIcon={<FiPlus />} onClick={() => setShowCreateModal(true)}>
              Create Grade
            </Button>
          </EmptyState>
        ) : (
          <GradeGrid>
            {grades.map((grade) => (
              <GradeCard key={grade.id}>
                <GradeCardHeader>
                  <div>
                    <h3>{grade.name}</h3>
                    <span className="badge">Level {grade.level}</span>
                  </div>
                </GradeCardHeader>
                
                <GradeCardBody>
                  <p>{grade.description}</p>
                  <div className="meta">
                    <span>📊 Level {grade.level}</span>
                    <span>✅ Active</span>
                  </div>
                </GradeCardBody>
                
                <GradeCardFooter>
                  <GradeActions>
                    <ActionButton 
                      title="Edit Grade"
                      onClick={() => handleEditGrade(grade)}
                    >
                      <FiEdit />
                    </ActionButton>
                    <ActionButton 
                      title="Delete Grade"
                      onClick={() => handleDeleteGrade(grade.id, grade.name)}
                    >
                      <FiTrash2 />
                    </ActionButton>
                  </GradeActions>
                </GradeCardFooter>
              </GradeCard>
            ))}
          </GradeGrid>
        )}

        {/* Create/Edit Grade Modal */}
        <Modal
          isOpen={showCreateModal}
          onClose={resetForm}
          title={editingGrade ? "Edit Grade" : "Create New Grade"}
          size="medium"
        >
          <form onSubmit={editingGrade ? handleUpdateGrade : handleCreateGrade}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <Input
                label="Grade Name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Grade 1, Grade 2"
                required
              />
              
              
              
              
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px' }}>
                <Button variant="ghost" onClick={resetForm} type="button">
                  Cancel
                </Button>
                <Button type="submit" loading={creating}>
                  {editingGrade ? 'Update Grade' : 'Create Grade'}
                </Button>
              </div>
            </div>
          </form>
        </Modal>
      </DashboardContainer>
    </DashboardLayout>
  );
};

export default TeacherGrades;
